import {
    generateClient,
    GraphQLQuery,
} from 'aws-amplify/api';

import {
    getDnsZone,
    getProviderConnectionCapabilities,
    listDnsZones,
} from './queries';

import {
    DnsZone,
    DnsZonesConnectionResult,
    GetDnsZone,
    ListDnsZones,
    ZoneWriteCapabilities,
} from '../types';

const client = generateClient();

// FNP-1191 — READ HALF. No add, no update, no delete: the FNP-1184 schema has
// no zone mutation to call, and the port methods behind one do not exist yet.
//
// ⚠️ Errors are RE-THROWN, not swallowed into a notification here — the same
// call the hosted-zones module makes and for the same reason. Catching and
// returning undefined makes a failed fetch indistinguishable from an empty
// result to React Query: `isError` stays false and the table renders its empty
// state, so "the API is down" and "you have no zones" look identical.
//
// ⚠️ A TRANSPORT ERROR AND A `failures[]` ENTRY ARE DIFFERENT THINGS and this
// module must not merge them. A rejection means the query itself did not
// answer — nothing is known. A `failures` entry means the query DID answer:
// these zones are real and complete, AND these connections could not be read.
// The first is an error state; the second is success-with-a-warning.

export const fetchRecords = async (
    connectionId: string | undefined,
    limit: number,
    nextToken: string | null,
): Promise<DnsZonesConnectionResult> => {

    const response = await client.graphql<GraphQLQuery<ListDnsZones>>({
        query: listDnsZones,
        variables: {
            connectionId,
            limit,
            nextToken,
        },
    });

    return {
        items: response.data?.listDnsZones?.items ?? [],
        // ⚠️ `?? null` and NOT `|| null`: an empty-string token is not a thing
        // this API returns, but coercing one to null would end pagination early.
        nextToken: response.data?.listDnsZones?.nextToken ?? null,
        // The schema says `[DnsConnectionFailure!]!` — empty, never null. The
        // default is here anyway so a partial/degraded response cannot make
        // `failures.length` throw in the banner.
        failures: response.data?.listDnsZones?.failures ?? [],
    };
};

// ⚠️ BOTH arguments, always. `getDnsZone(connectionId: ID!, id: ID!)` — passing
// the id alone is a schema validation error, not a lookup that returns null.
export const fetch = async (
    connectionId: string,
    id: string,
): Promise<DnsZone | undefined> => {

    const response = await client.graphql<GraphQLQuery<GetDnsZone>>({
        query: getDnsZone,
        variables: {
            connectionId,
            id,
        },
    });

    return response.data?.getDnsZone;
};

type GetProviderConnectionCapabilities = {
    getProviderConnection?: {
        id: string,
        status: string,
        capabilities?: ZoneWriteCapabilities | null,
    } | null
}

// The capability snapshot for ONE connection (FNP-1181).
//
// This is the read half of the capability-driven hiding rule: the CRUD ticket
// drops controls in behind `useZoneWriteCapabilities`, and this is what tells
// it whether to. Undefined means "could not be determined" and the hook treats
// that as NOT permitted — see useZoneWriteCapabilities for why that direction.
export const fetchConnectionCapabilities = async (
    connectionId: string,
): Promise<ZoneWriteCapabilities | undefined> => {

    const response = await client.graphql<GraphQLQuery<GetProviderConnectionCapabilities>>({
        query: getProviderConnectionCapabilities,
        variables: {id: connectionId},
    });

    const connection = response.data?.getProviderConnection;

    if (!connection) {
        return undefined;
    }

    // ⚠️ A connection that is not ACTIVE reports NO write capability, whatever
    // its stored snapshot says. The snapshot records what the credential proved
    // AT CONNECT TIME; an INVALID or UNREACHABLE status is the provider saying
    // that credential does not work NOW. Offering a write control against it
    // would produce a failure at submit rather than an honest absence.
    if (connection.status !== 'ACTIVE') {
        return undefined;
    }

    return connection.capabilities ?? undefined;
};
